import { createContext } from 'react';
import type { Region } from '@/types/documentModel';

export type DocumentViewerSource =
  | { kind: 'lecture'; lectureId: string }
  | { kind: 'chatDocument'; documentId: string };

/**
 * The chat surface a document belongs to, fixed when it opens: the AI desktop
 * (the full-page chat at `/` on desktop web) or the companion (the floating
 * shell, the study drawer's hand-off to it, or the phone sheet). Only that
 * surface renders, fetches or publishes it, so a document never shows on the
 * other one, even for the render before a navigation closes it.
 */
export type ChatDocumentHost = 'desktop' | 'companion';

/**
 * The exam a lecture belongs to, for a locked lecture's verify-your-school
 * action. A trigger that knows it passes it (View Document); otherwise the
 * viewer looks it up when the lecture turns out to be locked.
 */
export interface LectureDocumentExam {
  examId: string;
  schoolDomains?: string[];
}

/**
 * What the pane beside the document shows: the conversation (`chat`, the
 * default) or, for a lecture opened from its summary's citations, that
 * summary (`summary`), so the student reads both side by side.
 */
export type ChatDocumentSidePane = 'chat' | 'summary';

export interface DocumentViewerOpenOptions {
  page?: number;
  exam?: LectureDocumentExam | null;
  /**
   * A lecture page opened from its document model (a summary page chip, a
   * `document_page` citation that carries them): the bare fact keys it cites
   * and their boxes, which the viewer highlights. Without a `page`, the
   * viewer opens at the first region's page.
   */
  factIds?: string[];
  regions?: Region[];
  /**
   * mcgraw-hill: a passage to highlight by its words (a reference source's
   * excerpt), for documents without a document model's regions.
   */
  highlightText?: string;
  /**
   * The pane beside the document for this request; `chat` when absent. Set
   * on every request, so only the summary's own citations keep it on the
   * summary.
   */
  sidePane?: ChatDocumentSidePane;
}

/**
 * What the viewer highlights for one open request. `page` is the page the
 * request asked for: its regions are the active ones, and the item's regions
 * on other pages are shown as belonging to it.
 */
export interface DocumentHighlightRequest {
  page: number | null;
  factIds: string[];
  regions: Region[];
  /** mcgraw-hill: a passage on `page` to find in its text and highlight. */
  text?: string;
  /**
   * `item`: the regions of one mapped item (the StudyPanel's current
   * question). Every box shows as the item's (`all-for-item`); none is
   * singled out as the one just tapped.
   */
  scope?: 'item';
}

export interface ChatDocumentViewerCurrent {
  source: DocumentViewerSource;
  requestedPage: number | null;
  /**
   * The highlights the latest request asked for; absent for a request
   * without any (a plain page citation), which clears them.
   */
  requestedHighlight?: DocumentHighlightRequest;
  requestId: number;
  host: ChatDocumentHost;
  /**
   * The lecture a View Document session is on, or null for a document opened
   * beside the current conversation. From the moment the session opens the
   * lecture is claimed as the companion's surface anchor, which moves the
   * companion to the lecture's own thread; the claim ends when the viewer
   * closes.
   */
  sessionLectureId: string | null;
  /** Known for a lecture opened by a trigger that had it; see the type. */
  lectureExam?: LectureDocumentExam | null;
  /** What the pane beside the document shows; see the type. */
  sidePane: ChatDocumentSidePane;
}

export interface ChatDocumentViewerContextValue {
  current: ChatDocumentViewerCurrent | null;
  /** Open a document beside the current conversation. */
  open: (
    source: DocumentViewerSource,
    options?: DocumentViewerOpenOptions
  ) => void;
  /**
   * View Document: open a lecture on the companion together with the
   * lecture's own conversation (a new chat the first time), for as long as
   * the document stays open.
   */
  openLectureSession: (
    lectureId: string,
    options?: DocumentViewerOpenOptions
  ) => void;
  close: () => void;
  /** Switch the pane beside the open document (the summary's Chat button). */
  setSidePane: (pane: ChatDocumentSidePane) => void;
}

/** Outside the provider (the dev chat page, isolated tests) nothing opens. */
export const ChatDocumentViewerContext =
  createContext<ChatDocumentViewerContextValue>({
    current: null,
    open: () => {},
    openLectureSession: () => {},
    close: () => {},
    setSidePane: () => {},
  });

export const isSameDocumentViewerSource = (
  left: DocumentViewerSource,
  right: DocumentViewerSource
): boolean =>
  left.kind === right.kind &&
  (left.kind === 'lecture' && right.kind === 'lecture'
    ? left.lectureId === right.lectureId
    : left.kind === 'chatDocument' && right.kind === 'chatDocument'
      ? left.documentId === right.documentId
      : false);
